import { GAME, BACCARAT_BETTING_AREA } from 'utils/defines';
import BigNumber from 'bignumber.js';
import _last from 'lodash/last';
import _groupBy from 'lodash/groupBy';
import cloneDeep from 'utils/cloneDeep';
import config from 'config';

const CLEAR = 'game/history/CLEAR';

const LOAD = 'game/history/LOAD';
const LOAD_SUCCESS = 'game/history/LOAD_SUCCESS';
const LOAD_FAIL = 'game/history/LOAD_FAIL';
const LOAD_LAST = 'game/history/LOAD_LAST';
const LOAD_LAST_SUCCESS = 'game/history/LOAD_LAST_SUCCESS';
const LOAD_LAST_FAIL = 'game/history/LOAD_LAST_FAIL';
const LOAD_DETAIL = 'game/history/LOAD_DETAIL';
const LOAD_DETAIL_SUCCESS = 'game/history/LOAD_DETAIL_SUCCESS';
const LOAD_DETAIL_FAIL = 'game/history/LOAD_DETAIL_FAIL';

const SEARCH = 'game/history/SEARCH';
const SEARCH_SUCCESS = 'game/history/SEARCH_SUCCESS';
const SEARCH_FAIL = 'game/history/SEARCH_FAIL';

const UPDATE_REWARD = 'game/history/UPDATE_REWARD';
const SET_OPEN = 'game/history/SET_OPEN';

const GET_GAME = 'game/history/GET_GAME';
const GET_GAME_SUCCESS = 'game/history/GET_GAME_SUCCESS';
const GET_GAME_FAIL = 'game/history/GET_GAME_FAIL';

const LOGOUT = 'game/auth/LOGOUT';

const initialState = {
  loaded: false,
  hasMore: true,
  data: [],
  highrollers: [],
  mygames: [],
  local: [],
  fairness: [],
  result: [],
  search: {
    loaded: false,
    hasMore: true,
    data: {},
  },
  detail: {
    loaded: false,
    data: {},
  },
  open: false,
  reward: {},
  statistics: {
    [BACCARAT_BETTING_AREA._BANKER]: null,
    [BACCARAT_BETTING_AREA._PLAYER]: null,
    [BACCARAT_BETTING_AREA._BANKER_PAIR]: null,
    [BACCARAT_BETTING_AREA._PLAYER_PAIR]: null,
    [BACCARAT_BETTING_AREA._TIE]: null,
  },
};

export const toScore = scenario => {
  const getScore = str => str.split('').reduce((acc, cur) => {
    let val = 0;
    if (!Number.isNaN(+cur[0])) {
      val = +cur[0];
    } else if (cur[0] === 'A') {
      val = 1;
    }
    return (acc + val) % 10;
  }, 0);

  const score = {};
  Object.entries(scenario).forEach(([key, value]) => {
    score[key] = getScore(value);
  });

  score.winner = Math.max(...Object.values(score));
  return score;
};

const toData = ({
  players, scenario, result, seed, ...rest
}) => {
  let winnerBalance = new BigNumber(0);

  const playerToWinner = ({
    txid, payout_txid: payoutTxId, payout, ...playerRest
  }) => {
    winnerBalance = winnerBalance.plus(payout);

    return {
      tx_id: txid,
      tx_id_url: `${config.explorerTx}/${txid}`,
      ...(payoutTxId && { payout_tx_id_url: `${config.explorerTx}/${payoutTxId}` }),
      payout_balance: payout,
      ...playerRest,
    };
  };

  const score = scenario && toScore(scenario);
  if (!result && score?.player !== undefined && score?.banker !== undefined) {
    result = 'T';
    if (score.player !== score.banker) {
      result = score.player > score.banker ? 'P' : 'B';
    }
  }

  return {
    ...(Array.isArray(players) && { winner_list: players.map(playerToWinner) }),
    winner_balance: winnerBalance.toFixed(),
    scenario,
    ...(scenario && { score }),
    result,
    game_result: result,
    ...(seed && { seed_hash: seed }),
    ...rest,
  };
};

export function makePair(_games) {
  const games = cloneDeep(_games);
  for (let index = games.length - 1; index >= 0; index -= 1) {
    if (Array.isArray(games[index].result)) {
      let newResult = null;
      if (games[index].result.length > 1) {
        const winnerByArea = _groupBy(games[index].winner_list, 'area');
        const add = result => {
          const data = {
            ...games[index],
            result,
            game_id: `!${result}-${games[index].game_id}`,
            game_result: result,
            winner_list: winnerByArea[result] || [],
          };
          data.winner_balance = new BigNumber(0);
          // eslint-disable-next-line camelcase
          data.winner_list.forEach(({ payout_balance }) => {
            data.winner_balance = data.winner_balance.plus(payout_balance);
          });
          data.winner_balance = data.winner_balance.toFixed();
          games.splice(index + 1, 0, data);
        };
        games[index].result.forEach(result => {
          if (['P', 'B', 'T'].indexOf(result) !== -1) {
            newResult = result;
          }
        });
        if (games[index].result.indexOf('BP') !== -1) {
          add('BP');
        }
        if (games[index].result.indexOf('PP') !== -1) {
          add('PP');
        }
        games[index].winner_list = winnerByArea[newResult];
        games[index].winner_balance = new BigNumber(0);
        // eslint-disable-next-line camelcase
        (games[index].winner_list || []).forEach(({ payout_balance }) => {
          games[index].winner_balance = games[index].winner_balance.plus(payout_balance);
        });
        games[index].winner_balance = games[index].winner_balance.toFixed();
      } else {
        [newResult] = games[index].result;
      }

      games[index].result = newResult;
      games[index].game_result = newResult;
    }
  }

  return games;
}

export default function history(state = initialState, action = {}) {
  switch (action.type) {
    case CLEAR: {
      if (!action.key) {
        return {
          ...initialState,
        };
      }
      return {
        ...state,
        [action.key]: [],
      };
    }

    case LOAD:
    case LOAD_LAST:
      return {
        ...state,
        loading: true,
      };
    case LOAD_FAIL:
      return {
        ...state,
      };
    case LOAD_LAST_FAIL:
      return {
        ...state,
        loading: false,
        loaded: false,
        error: action.error,
      };
    case LOAD_SUCCESS: {
      const key = action.key ? action.key : 'data';

      const data = key === 'result' ? action.result.data.results : [...state[key]];

      const resultData = [...(action.result.data?.results ? action.result.data.results : action.result.data)].map(
        toData
      );

      const hasMore = resultData.length > 0 && action.page < 20;
      let statistics = null;

      if (key !== 'result' && hasMore) {
        const loadedFirstGameId = resultData?.[0]?.game_id; // eslint-disable-line
        const beforeLastGameId = (_last(state[key]) || {}).game_id || +loadedFirstGameId + 1;

        data.push(
          ...resultData.splice(loadedFirstGameId < beforeLastGameId ? 0 : loadedFirstGameId - beforeLastGameId + 1)
        );
      }

      if (action.page === 0 && action.game === GAME._BACCARAT && !action.verify) {
        statistics = {
          [BACCARAT_BETTING_AREA._BANKER]: 0,
          [BACCARAT_BETTING_AREA._PLAYER]: 0,
          [BACCARAT_BETTING_AREA._BANKER_PAIR]: 0,
          [BACCARAT_BETTING_AREA._PLAYER_PAIR]: 0,
          [BACCARAT_BETTING_AREA._TIE]: 0,
        };
        for (let index = 0; index < 100; index += 1) {
          data[index].result.forEach(res => {
            statistics[res] += 1;
          });
        }
      }

      return {
        ...state,
        loading: false,
        loaded: true,
        hasMore,
        [key]: data,
        ...(statistics && { statistics }),
      };
    }
    case LOAD_LAST_SUCCESS: {
      let newData = (action.result.data?.results ? action.result.data?.results : action.result.data).map(toData);
      let statistics = null;

      // makePair(newData);
      newData = newData.map(data => ({
        ...data,
        winner_list: (data.winner_list || []).map(_winner => {
          const winner = { ..._winner };
          if (!winner.payout_tx_id_url) {
            const payoutTxId = state.reward[[winner.tx_id, winner.area].filter(item => !!item).join('-')];
            if (payoutTxId) {
              winner.payout_tx_id_url = `${config.explorerTx}/${payoutTxId}`;
            }
          }
          return winner;
        }),
      }));

      if (action.game === GAME._BACCARAT) {
        statistics = Object.assign({}, state.statistics);
        // Required when the number of newly received games is not 1.
        newData[0].result.forEach(res => {
          statistics[res] += 1;
        });
        state.data[99].result.forEach(res => {
          statistics[res] -= 1;
        });
      }

      const mygames = [...state.mygames];
      const local = [...state.local];
      const highrollers = [...state.highrollers];
      const result = [...state.result];
      const data = [...newData, ...state.data];

      if (action.game === GAME._DICE) {
        if (action.mine) {
          mygames.unshift(newData[0]);
        }
        if (action.local) {
          local.unshift(newData[0]);
        }
        if (action.high) {
          highrollers.unshift(newData[0]);
        }
        result.unshift(newData[0].game_result);
      }

      return {
        ...state,
        loading: false,
        loaded: true,
        data,
        highrollers,
        local,
        mygames,
        result,
        ...(statistics && { statistics }),
      };
    }

    case SEARCH:
      return {
        ...state,
        search: {
          ...state.search,
          loading: true,
        },
      };
    case SEARCH_FAIL:
      return {
        ...state,
        search: {
          ...state.search,
          loading: false,
          loaded: false,
          error: action.error,
        },
      };
    case SEARCH_SUCCESS: {
      const newData = { ...action.result.data };
      // eslint-disable-next-line camelcase
      if (newData?.is_refunded === 'Y' && !newData?.players) {
        // eslint-disable-next-line camelcase
        const refund_txid = newData?.refund_txid;
        newData.players = [
          {
            is_refunded: 'Y',
            // eslint-disable-next-line camelcase
            ...(refund_txid && { refund_txid }),
          },
        ];
      }
      if (Array.isArray(newData.players?.[0]?.result)) {
        let newResult;
        for (let i = 0; i < newData.players[0].result.length; i += 1) {
          if (['P', 'B', 'T'].indexOf(newData.players[0].result[i]) !== -1) {
            newResult = newData.players[0].result[i];
            break;
          }
        }

        if (newResult) {
          newData.players.forEach(player => {
            player.result = newResult;
          });
        }
      }

      if (newData.players && Array.isArray(newData.players)) {
        newData.players.forEach(player => {
          player.payout_txid_url = `${config.explorerTx}/${player.payout_txid}`;
        });
      }

      return {
        ...state,
        search: {
          ...state.search,
          loading: false,
          loaded: true,
          data: newData,
        },
      };
    }

    case GET_GAME:
    case LOAD_DETAIL:
      return {
        ...state,
        detail: {
          ...state.detail,
          loading: true,
        },
      };

    case GET_GAME_FAIL:
    case LOAD_DETAIL_FAIL:
      return {
        ...state,
        detail: {
          ...state.detail,
          loading: false,
          loaded: false,
        },
      };

    case GET_GAME_SUCCESS:
    case LOAD_DETAIL_SUCCESS:
      return {
        ...state,
        detail: {
          ...state.detail,
          loading: false,
          loaded: true,
          data: action.result?.data?.results?.[0] || action.result?.data,
        },
      };
    case UPDATE_REWARD: {
      const newData = [...state.data];
      const {
        game, value: {
          gameId, playerTxId, payoutTxId, area,
        } = {},
      } = action?.data;
      let updated = false;

      // `!${result}-${games[index].game_id}`
      for (let dataIdx = 0; dataIdx < newData.length; dataIdx += 1) {
        if (newData[dataIdx].game_id === gameId) {
          for (let winnerIdx = 0; winnerIdx < newData[dataIdx].winner_list.length; winnerIdx += 1) {
            if (
              newData[dataIdx].winner_list[winnerIdx].tx_id === playerTxId
              && (game !== GAME._BACCARAT || newData[dataIdx].winner_list[winnerIdx].area === area)
            ) {
              newData[dataIdx].winner_list[winnerIdx].payout_tx_id = payoutTxId;
              newData[dataIdx].winner_list[
                winnerIdx
              ].payout_tx_id_url = `${config.explorerTx}/${newData[dataIdx].winner_list[winnerIdx].payout_tx_id}`;
              updated = true;
              break;
            }
          }
          break;
        }
      }

      return {
        ...state,
        data: newData,
        // TODO After using the reward information accumulated here, you must erase it.
        ...(!updated && {
          reward: { ...state.reward, [[playerTxId, area].filter(item => item).join('-')]: payoutTxId },
        }),
        ...(state.search?.data?.query === playerTxId && {
          search: {
            ...state.search,
            data: {
              ...state.search.data,
              payout_tx_id: payoutTxId,
              payout_tx_id_url: `${config.explorerTx}/${payoutTxId}`,
            },
          },
        }),
      };
    }
    case SET_OPEN:
      return {
        ...state,
        open: action.open,
      };
    case LOGOUT: {
      return {
        ...state,
        mygames: [],
      };
    }
    default:
      return state;
  }
}

export function isLoaded(globalState) {
  return globalState.history && globalState.history.loaded;
}

export function clear() {
  return {
    type: CLEAR,
  };
}

export function clearLocal() {
  return {
    type: CLEAR,
    key: 'local',
  };
}

export function load({ page = 0, size = 50 } = {}) {
  return (dispatch, getState) => {
    const state = getState();
    const { game } = state.info;
    let url = `/${game}/game/history`;
    if (game === 'dice') {
      url = `/v1${url}`;
    }
    return dispatch({
      types: [LOAD, LOAD_SUCCESS, LOAD_FAIL],
      promise: ({ client }) => client.get(url, {
        params: {
          asset: config.asset,
          size,
          page,
        },
      }),
      page,
    });
  };
}

export function loadLast() {
  return (dispatch, getState) => {
    const state = getState();
    const { game } = state.info;
    let url = `/${game}/game/history`;
    if (game === 'dice') {
      url = `/v1${url}`;
    }
    return dispatch({
      types: [LOAD_LAST, LOAD_LAST_SUCCESS, LOAD_LAST_FAIL],
      promise: ({ client }) => client.get(url, {
        params: {
          asset: config.asset,
          size: 1,
        },
      }),
    });
  };
}

export function loadDetail(gameId) {
  return (dispatch, getState) => {
    const state = getState();
    return dispatch({
      types: [LOAD_DETAIL, LOAD_DETAIL_SUCCESS, LOAD_DETAIL_FAIL],
      promise: ({ client }) => client.get(`/${state.info.game}/search/previous/detail`, {
        params: {
          asset: config.asset,
          game_id: gameId,
        },
      }),
    });
  };
}

export function search({ q }) {
  return (dispatch, getState) => {
    const state = getState();
    return dispatch({
      types: [SEARCH, SEARCH_SUCCESS, SEARCH_FAIL],
      promise: ({ client }) => client.get(`/${state.info.game}/search/previous`, {
        params: {
          asset: config.asset,
          q,
        },
      }),
    });
  };
}

export function searchFromTxId(txid) {
  return (dispatch, getState) => {
    const state = getState();
    const { game } = state.info;
    const version = game === 'dice' ? 'v1' : 'v2';
    return dispatch({
      types: [SEARCH, SEARCH_SUCCESS, SEARCH_FAIL],
      promise: ({ client }) => client.get(`/${version}/${game}/bets/${txid}`, {
        params: {
          asset: config.asset,
        },
      }),
    });
  };
}

export function updateReward(data) {
  return {
    type: UPDATE_REWARD,
    data,
  };
}

export function loadGames({
  asset = 'BSV', count = 100, page = 0, verify = false, key, ...args
} = {}) {
  return (dispatch, getState) => {
    const state = getState();
    const { game } = state.info;
    const version = verify || game === 'dice' ? 'v1' : 'v2';
    let endpoint = 'games';
    if (verify || key === 'fairness') {
      endpoint = 'fairness';
    } else if (key === 'highrollers') {
      endpoint += `/${key}`;
    } else if (key === 'mygames') {
      endpoint = key;
    } else if (key === 'result') {
      endpoint = 'games/result';
    }
    return dispatch({
      types: [LOAD, LOAD_SUCCESS, LOAD_FAIL],
      promise: ({ client }) => client.get(`/${version}/${game}/${endpoint}`, {
        params: {
          asset,
          count,
          // offset: 46908,
          ...args,
        },
      }),
      page,
      verify,
      game,
      key,
    });
  };
}

export function loadLastGame({
  asset = 'BSV', userIdHash = null, offset, local = false,
} = {}) {
  return (dispatch, getState) => {
    const state = getState();
    const { game } = state.info;
    const version = game === 'dice' ? 'v1' : 'v2';
    const mine = userIdHash && userIdHash === state.auth.user?.hashedId;
    return dispatch({
      types: [LOAD_LAST, LOAD_LAST_SUCCESS, LOAD_LAST_FAIL],
      promise: ({ client }) => client.get(`/${version}/${game}/games`, {
        params: {
          offset,
          asset,
          count: 1,
        },
      }),
      game,
      mine,
      local,
    });
  };
}

export function addGame(data, { local = false, high = false }) {
  return (dispatch, getState) => {
    const state = getState();
    const { game } = state.info;
    const mine = data.players[0].user_hash === state.auth.user?.hashedId;
    return dispatch({
      type: LOAD_LAST_SUCCESS,
      result: { data: [data] },
      game,
      mine,
      local,
      high,
    });
  };
}

export function setOpen(open) {
  return {
    type: SET_OPEN,
    open,
  };
}

export function getGame(gameId) {
  return (dispatch, getState) => {
    const state = getState();
    return dispatch({
      types: [GET_GAME, GET_GAME_SUCCESS, GET_GAME_FAIL],
      promise: ({ client }) => client.get(`/v1/${state.info.game}/fairness`, {
        params: {
          asset: 'BSV',
          count: 1,
          offset: gameId,
        },
      }),
      gameId,
    });
  };
}
